import PropTypes from 'prop-types';
import { forwardRef, useImperativeHandle } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import Button from 'shopper/components/Button';
import Input from 'shopper/components/Input';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';
import { isEmailValid } from 'lib/string';

import Divider from './Divider';
import SocialMediaLogin from './SocialMediaLogin';

import LOGIN from 'constants/login';

const EmailStep = forwardRef(
  (
    {
      gaEventCategory,
      greetingsMessage,
      goToSignInStep,
      goToSignUpStep,
      onClose,
    },
    ref
  ) => {
    const { control, trigger } = useFormContext();

    /**
     * Set forwarded ref as `null` only to parent know that this component not
     * have internal function utils like `<SignUpStep />` and remove errors
     * related of non-use of `forwardRef`.
     */
    useImperativeHandle(ref, () => null);

    const checkAndGo = (callback) => {
      return async () => {
        const isFieldsValid = await trigger('email');

        if (isFieldsValid) {
          callback();
        }
      };
    };

    return (
      <>
        <SocialMediaLogin
          gaEventCategory={gaEventCategory}
          greetingsMessage={greetingsMessage}
          onClose={onClose}
        />
        <Divider className="my-4" size="size3" />
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState: { error } }) => (
            <Input
              error={!!error}
              message={error?.message}
              placeholder="Ex: seunome@email.com"
              type="email"
              full
              {...field}
            />
          )}
          rules={{
            required: LOGIN.EMAIL_REQUIRED,
            validate: (value) => isEmailValid(value) || LOGIN.EMAIL_NOT_VALID,
          }}
        />
        <Button
          className="mb-4 mt-10"
          htmlType="button"
          size="size2"
          full
          onClick={checkAndGo(() => {
            sendEvent({
              action: 'signup_appear',
              category: gaEventCategory,
            });
            goToSignUpStep();
          })}
        >
          {placeholder('actions.signupShort2')}
        </Button>
        <Button
          htmlType="button"
          size="size2"
          type="primary-ghost"
          full
          onClick={checkAndGo(() => {
            sendEvent({
              action: 'login_appear',
              category: gaEventCategory,
            });
            goToSignInStep();
          })}
        >
          {placeholder('actions.signin')}
        </Button>
      </>
    );
  }
);

EmailStep.displayName = 'EmailStep';

EmailStep.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  greetingsMessage: PropTypes.string.isRequired,
  goToSignInStep: PropTypes.func.isRequired,
  goToSignUpStep: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EmailStep;
